import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Image as ImageIcon, Loader2, X } from "lucide-react";
import { getApiBase, authedBlobUrl } from "@/api/client";
import { cn } from "@/lib/cn";

// Screenshot thumbnail + lightbox.
// Real runs: pass `runId` + `stepIndex` → fetches the auth-protected endpoint
//   GET /test/runs/{runId}/screenshots/{stepIndex} as a blob (a plain <img>
//   can't send the Bearer token).
// Demo/direct: pass `src` (a URL) instead.
// Which runner owns the run. All three store screenshots under their own
// prefix; hardcoding /test meant an exe or extension case showed a broken image
// wherever it was rendered outside its own runner page.
const SHOT_BASE = { cloud: "/test", exe: "/exe", extension: "/extension" };

export default function ScreenshotViewer({
  runId, stepIndex, src, label = "screenshot", className, kind = "cloud",
}) {
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const hasEndpoint = runId != null && stepIndex != null;

  useEffect(() => {
    if (!hasEndpoint) return undefined;
    let active = true;
    let created = null;
    setLoading(true);
    authedBlobUrl(`${SHOT_BASE[kind] || SHOT_BASE.cloud}/runs/${runId}/screenshots/${stepIndex}`).then((url) => {
      if (!active) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      created = url;
      setBlobUrl(url);
      setLoading(false);
    });
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [hasEndpoint, runId, stepIndex, kind]);

  const directSrc = src
    ? src.startsWith("http")
      ? src
      : `${getApiBase()}/${src.replace(/^\//, "")}`
    : null;
  const resolved = blobUrl || directSrc;

  const close = useCallback(() => setOpen(false), []);

  // Escape closes, and the page behind the lightbox must not scroll under it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  return (
    <>
      <button
        type="button"
        onClick={() => resolved && setOpen(true)}
        title={resolved ? "View screenshot" : "No screenshot"}
        className={cn(
          "grid h-12 w-16 shrink-0 place-items-center overflow-hidden rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 dark:from-white/10 dark:to-white/5",
          resolved && "cursor-zoom-in",
          className
        )}
      >
        {resolved ? (
          <img src={resolved} alt={label} className="h-full w-full object-cover" />
        ) : loading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <ImageIcon size={16} />
        )}
      </button>

      <AnimatePresence>
        {open && resolved && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={close}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            // No animated backdrop-blur — the solid scrim keeps the open smooth.
            className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/85 p-6"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={close}
              className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-xl bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <X size={20} />
            </button>
            {/* The scrim closes on click, so the image has to stop the event:
                without this, clicking the screenshot you just opened dismissed
                it, and there was no way to right-click/save or zoom into it. */}
            <img
              src={resolved}
              alt={label}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full cursor-default rounded-xl shadow-glow"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
